import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import ChildrenPage from './pages/ChildrenPage';
import CompletedWorkPage from './pages/CompletedWorkPage';
import GoalsPage from './pages/GoalsPage';
import LessonPlansPage from './pages/LessonPlansPage';
import SettingsPage from './pages/SettingsPage';
import StateRequirementsPage from './pages/StateRequirementsPage';
import PendingWorkPage from './pages/PendingWorkPage';
import ParentGradesPage from './pages/ParentGradesPage';
import SubjectAssignmentsPage from './pages/SubjectAssignmentsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionCancelledPage from './pages/SubscriptionCancelledPage';
import ChildLoginPage from './pages/ChildLoginPage';
import ChildDashboard from './pages/ChildDashboard';
import ChildSubmitWork from './pages/ChildSubmitWork';
import ChildRewardsStore from './pages/ChildRewardsStore';
import ChildAssignments from './pages/ChildAssignments';
import ChildGrades from './pages/ChildGrades';
import ChildGoalsPage from './pages/ChildGoalsPage';
import ChildCollectionPage from './pages/ChildCollectionPage';
import ChildProtectedRoute from './components/ChildProtectedRoute';
import ProgressReportPage from './pages/ProgressReportPage';
import ReportCardPage from './pages/ReportCardPage';
import StandardsReportPage from './pages/StandardsReportPage';
import RewardAdminPage from './pages/RewardAdminPage';
import RewardManagerPage from './pages/RewardManagerPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminSetupPage from './pages/AdminSetupPage';
import AdminPanelPage from './pages/AdminPanelPage';
import UserManagementPage from './pages/UserManagementPage';
import AdminStandardsPage from './pages/AdminStandardsPage';
import ClearCachePage from './pages/ClearCachePage';
import BulkRewardUpload from './pages/BulkRewardUpload';
import PurchaseManagerPage from './pages/PurchaseManagerPage';
import OrderStickersPage from './pages/OrderStickersPage';
import StickerCheckoutPage from './pages/StickerCheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import OrderCancelledPage from './pages/OrderCancelledPage';
import StudentOverviewPage from './pages/StudentOverviewPage';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppContent() {
  const location = useLocation();
  const isChildRoute = location.pathname.startsWith('/child');
  const isAuthRoute = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/';

  return (
    <>
      {!isChildRoute && !isAuthRoute && <Header />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Parent Routes - Protected */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/student/:childId" element={<ProtectedRoute><StudentOverviewPage /></ProtectedRoute>} />
        <Route path="/children" element={<ProtectedRoute><ChildrenPage /></ProtectedRoute>} />
        <Route path="/completed-work" element={<ProtectedRoute><CompletedWorkPage /></ProtectedRoute>} />
        <Route path="/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
        <Route path="/grades" element={<ProtectedRoute><ParentGradesPage /></ProtectedRoute>} />
        <Route path="/lesson-plans" element={<ProtectedRoute><LessonPlansPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/requirements" element={<ProtectedRoute><StateRequirementsPage /></ProtectedRoute>} />
        <Route path="/pending" element={<ProtectedRoute><PendingWorkPage /></ProtectedRoute>} />
        <Route path="/subject-assignments" element={<ProtectedRoute><SubjectAssignmentsPage /></ProtectedRoute>} />
        
        {/* Subscription Routes */}
        <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
        <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
        <Route path="/subscription/cancelled" element={<ProtectedRoute><SubscriptionCancelledPage /></ProtectedRoute>} />

        {/* Sticker Order Routes */}
        <Route path="/order-stickers" element={<ProtectedRoute><OrderStickersPage /></ProtectedRoute>} />
        <Route path="/orders/stickers/checkout" element={<ProtectedRoute><StickerCheckoutPage /></ProtectedRoute>} />
        <Route path="/orders/success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
        <Route path="/orders/cancelled" element={<ProtectedRoute><OrderCancelledPage /></ProtectedRoute>} />

        {/* Child Routes - Protected */}
        <Route path="/child/login" element={<ChildLoginPage />} />
        <Route path="/child/dashboard" element={<ChildProtectedRoute><ChildDashboard /></ChildProtectedRoute>} />
        <Route path="/child/submit" element={<ChildProtectedRoute><ChildSubmitWork /></ChildProtectedRoute>} />
        <Route path="/child/rewards" element={<ChildProtectedRoute><ChildRewardsStore /></ChildProtectedRoute>} />
        <Route path="/child/assignments" element={<ChildProtectedRoute><ChildAssignments /></ChildProtectedRoute>} />
        <Route path="/child/grades" element={<ChildProtectedRoute><ChildGrades /></ChildProtectedRoute>} />
        <Route path="/child/goals" element={<ChildProtectedRoute><ChildGoalsPage /></ChildProtectedRoute>} />
        <Route path="/child/collection" element={<ChildProtectedRoute><ChildCollectionPage /></ChildProtectedRoute>} />
        
        {/* Report Pages */}
        <Route path="/progress-report" element={<ProgressReportPage />} />
        <Route path="/report-card" element={<ReportCardPage />} />
        <Route path="/standards-report" element={<StandardsReportPage />} />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/setup" element={<AdminSetupPage />} />
        <Route path="/admin/panel" element={<AdminPanelPage />} />
        <Route path="/admin/users" element={<UserManagementPage />} />
        <Route path="/admin/standards" element={<AdminStandardsPage />} />
        <Route path="/admin/rewards" element={<RewardAdminPage />} />
        <Route path="/admin/rewards/manage" element={<RewardManagerPage />} />
        <Route path="/admin/rewards/bulk-upload" element={<BulkRewardUpload />} />
        <Route path="/admin/purchases" element={<PurchaseManagerPage />} />
        <Route path="/admin/clear-cache" element={<ClearCachePage />} />
      </Routes>
      <Toaster />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;